import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SupportButton from "@/components/base/SupportButton";
import { Shield, Lock, Eye, Server } from "lucide-react";

export default function PrivacyPage() {
  const [darkMode, setDarkMode] = useState(false);

  return (
    <div className={darkMode ? "dark" : ""}>
      <div className={`min-h-screen ${darkMode ? "bg-slate-950 text-white" : "bg-white text-slate-900"}`}>
        <Navbar darkMode={darkMode} onToggleDark={() => setDarkMode(!darkMode)} />

        <main className="pt-24 pb-16 px-4 md:px-6 max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/40 rounded-full px-4 py-1.5 mb-4">
              <Shield size={14} className="text-blue-700 dark:text-blue-400" />
              <span className="text-xs font-semibold text-blue-800 dark:text-blue-300">Protección de Datos Garantizada</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 dark:text-white mb-3">Política de Privacidad</h1>
            <p className="text-slate-500 dark:text-slate-400 text-sm">Última actualización: 29 de mayo de 2026</p>
          </div>

          <div className="prose prose-slate dark:prose-invert max-w-none text-slate-600 dark:text-slate-300 space-y-8">
            <section className="bg-slate-50 dark:bg-slate-900/40 border border-slate-100 dark:border-slate-800/40 rounded-2xl p-6">
              <p className="text-sm leading-relaxed mb-0">
                En <strong>emite.do</strong>, propiedad de Codec SRL, nos tomamos muy en serio la seguridad y confidencialidad de tus datos. Esta Política de Privacidad describe cómo recopilamos, almacenamos, firmamos, procesamos y protegemos la información fiscal y los datos de tu empresa de acuerdo con la **Ley No. 172-13 sobre Protección de Datos de Carácter Personal** de la República Dominicana y las normativas vigentes de la Dirección General de Impuestos Internos (DGII).
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Lock className="text-blue-800 dark:text-blue-400" size={18} />
                1. Custodia de Certificados Digitales (P12)
              </h2>
              <p className="text-sm leading-relaxed">
                Para la emisión de Comprobantes Fiscales Electrónicos (e-CF), es obligatorio el uso de un certificado digital de persona física emitido por una entidad autorizada y registrado ante la DGII.
              </p>
              <ul className="list-disc pl-5 text-sm space-y-2">
                <li><strong>Cifrado Avanzado:</strong> Tu archivo de certificado digital `.p12` o `.pfx` y su respectivo PIN se almacenan en un depósito criptográfico seguro (Key Vault) cifrado bajo el algoritmo <strong>AES-256</strong> (cifrado de sobre/envelope encryption).</li>
                <li><strong>Aislamiento de Claves:</strong> emite.do nunca almacena ni expone tu PIN en texto plano ni en logs del sistema.</li>
                <li><strong>Uso Exclusivo:</strong> Los certificados se cargan en memoria efímera únicamente durante el proceso de firma digital de los archivos XML fiscales y para las consultas directas en los webservices oficiales de la DGII.</li>
              </ul>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Eye className="text-blue-800 dark:text-blue-400" size={18} />
                2. Información Recopilada y Procesada
              </h2>
              <p className="text-sm leading-relaxed">
                Nuestra plataforma procesa información estrictamente orientada al cumplimiento tributario y la facturación electrónica:
              </p>
              <ul className="list-disc pl-5 text-sm space-y-2">
                <li><strong>Datos de Empresa:</strong> RNC, Razón Social, Nombre Comercial, dirección física, correos de contacto y credenciales de acceso.</li>
                <li><strong>Datos Fiscales Transaccionales:</strong> Payloads JSON enviados desde tu ERP que contienen detalles de ventas, compras, RNC de compradores, ITBIS y montos transaccionales necesarios para estructurar los e-CF oficiales.</li>
                <li><strong>Registros de Auditoría (Audit Logs):</strong> Registro de acciones administrativas en tu cuenta (ej. rotación de llaves, carga de rangos NCF, modificaciones de perfil) para asegurar la trazabilidad.</li>
              </ul>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Server className="text-blue-800 dark:text-blue-400" size={18} />
                3. Retención y Almacenamiento de Datos
              </h2>
              <p className="text-sm leading-relaxed">
                De acuerdo con el Código Tributario de la República Dominicana y las regulaciones aplicables a los proveedores de facturación electrónica:
              </p>
              <ul className="list-disc pl-5 text-sm space-y-2">
                <li><strong>Historial Fiscal:</strong> emite.do retiene los XML firmados, representaciones impresas (PDF), respuestas de la DGII y acuses de recibo en almacenamiento redundante seguro durante un periodo mínimo de **10 años** para asegurar la disponibilidad ante fiscalizaciones tributarias.</li>
                <li><strong>Eliminación de Cuenta:</strong> Si decides dar de baja el servicio, tu certificado digital y PIN son purgados permanentemente de nuestros vaults de producción de forma inmediata. Los registros de transacciones fiscales históricas ya enviadas se mantendrán archivados bajo custodia inmutable para cumplir con los plazos legales tributarios.</li>
              </ul>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Shield className="text-blue-800 dark:text-blue-400" size={18} />
                4. Transferencia de Datos
              </h2>
              <p className="text-sm leading-relaxed">
                emite.do **no vende, no comparte ni comercializa** los datos de facturación de tu empresa ni de tus clientes con terceros. La única transferencia de datos que se realiza es el envío oficial obligatorio de los XML firmados hacia los servidores de la **Dirección General de Impuestos Internos (DGII)** y la distribución de los acuses a las empresas receptoras electrónicas en el flujo B2B regular.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                5. Derechos ARCO
              </h2>
              <p className="text-sm leading-relaxed">
                Tienes derecho a acceder, rectificar, cancelar u oponerte al procesamiento de tus datos no fiscales de contacto mediante una solicitud formal a nuestro correo electrónico:{" "}
                <a href="mailto:ecf@codec.do" className="text-blue-800 dark:text-blue-400 hover:underline">
                  ecf@codec.do
                </a>
                .
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                6. Publicación y contacto (Anexo 1.3 DGII)
              </h2>
              <p className="text-sm leading-relaxed">
                Esta página constituye la <strong>política de protección de datos pública</strong> de emite.do,
                publicada en{" "}
                <a href="https://www.emite.do/privacy" className="text-blue-800 dark:text-blue-400 hover:underline">
                  https://www.emite.do/privacy
                </a>
                , en cumplimiento del Anexo 1.3 de la documentación de autorización de Proveedores de Servicios de
                Facturación Electrónica de la DGII y de la Ley núm. 172-13.
              </p>
              <ul className="list-disc pl-5 text-sm space-y-2">
                <li>
                  <strong>Correo:</strong>{" "}
                  <a href="mailto:ecf@codec.do" className="text-blue-800 dark:text-blue-400 hover:underline">
                    ecf@codec.do
                  </a>
                </li>
                <li>
                  <strong>Teléfono:</strong>{" "}
                  <a href="tel:+18294570964" className="text-blue-800 dark:text-blue-400 hover:underline">
                    +1 (829) 457-0964
                  </a>
                </li>
                <li>
                  <strong>Seguridad (Anexo 1.1):</strong>{" "}
                  <a href="/seguridad" className="text-blue-800 dark:text-blue-400 hover:underline">
                    https://www.emite.do/seguridad
                  </a>
                </li>
              </ul>
            </section>
          </div>
        </main>

        <Footer darkMode={darkMode} />
        <SupportButton />
      </div>
    </div>
  );
}
